/**
 * Secure Image File Validation Utility
 * 
 * SECURITY NOTE: This utility uses magic bytes (file signatures) to detect
 * actual file formats, NOT file extensions or MIME types from the browser.
 * 
 * Why MIME validation is insecure:
 * 1. file.type (MIME) is provided by the browser and can be spoofed
 * 2. File extensions can be easily renamed (e.g., malware.exe -> image.png)
 * 3. accept="image/*" only filters the file picker UI, not actual file content
 * 
 * Magic bytes are the first few bytes of a file that identify its true format.
 * These cannot be easily spoofed without corrupting the file.
 */

export type ImageType = "png" | "jpeg" | "webp" | "gif";

export interface ImageValidationResult {
  isValid: boolean;
  type: ImageType | null;
  error?: string;
}

/**
 * Magic bytes (file signatures) for supported image formats
 * These are the first bytes that identify the actual file format
 */
const MAGIC_BYTES: Record<ImageType, readonly number[]> = {
  // PNG: 89 50 4E 47 0D 0A 1A 0A
  png: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] as const,
  
  // JPEG: FF D8 FF (multiple variants)
  jpeg: [0xff, 0xd8, 0xff] as const,
  
  // WEBP: RIFF ... WEBP
  // First 4 bytes: 52 49 46 46 (RIFF)
  // Bytes 8-11: 57 45 42 50 (WEBP)
  webp: [0x52, 0x49, 0x46, 0x46] as const, // RIFF header
  
  // GIF: 47 49 46 38 (GIF8)
  gif: [0x47, 0x49, 0x46, 0x38] as const,
};

/**
 * Reads the first bytes of a file to detect its actual format using magic bytes
 * 
 * @param file - The File object to validate
 * @returns Promise resolving to ImageType if valid image, null otherwise
 * 
 * @example
 * const type = await detectRealImageType(file);
 * if (type === null) {
 *   // File is not a valid image or is fake
 * }
 */
export async function detectRealImageType(file: File): Promise<ImageType | null> {
  // Read only the first 12 bytes (enough to detect all supported formats)
  // This is efficient even for large files
  const buffer = await file.slice(0, 12).arrayBuffer();
  const bytes = new Uint8Array(buffer);

  // Check PNG: 89 50 4E 47 0D 0A 1A 0A
  if (bytes.length >= 8) {
    const pngSignature = MAGIC_BYTES.png;
    let isPng = true;
    for (let i = 0; i < pngSignature.length; i++) {
      if (bytes[i] !== pngSignature[i]) {
        isPng = false;
        break;
      }
    }
    if (isPng) return "png";
  }

  // Check JPEG: FF D8 FF
  if (bytes.length >= 3) {
    const jpegSignature = MAGIC_BYTES.jpeg;
    if (
      bytes[0] === jpegSignature[0] &&
      bytes[1] === jpegSignature[1] &&
      bytes[2] === jpegSignature[2]
    ) {
      return "jpeg";
    }
  }

  // Check GIF: 47 49 46 38 (GIF8)
  if (bytes.length >= 4) {
    const gifSignature = MAGIC_BYTES.gif;
    let isGif = true;
    for (let i = 0; i < gifSignature.length; i++) {
      if (bytes[i] !== gifSignature[i]) {
        isGif = false;
        break;
      }
    }
    if (isGif) return "gif";
  }

  // Check WEBP: RIFF (52 49 46 46) ... WEBP (57 45 42 50)
  // Need at least 12 bytes to check WEBP signature
  if (bytes.length >= 12) {
    const webpRiff = MAGIC_BYTES.webp;
    const webpString = [0x57, 0x45, 0x42, 0x50]; // "WEBP"
    
    // Check RIFF header (first 4 bytes)
    let isRiff = true;
    for (let i = 0; i < webpRiff.length; i++) {
      if (bytes[i] !== webpRiff[i]) {
        isRiff = false;
        break;
      }
    }
    
    // Check WEBP string (bytes 8-11)
    if (isRiff) {
      let isWebp = true;
      for (let i = 0; i < webpString.length; i++) {
        if (bytes[8 + i] !== webpString[i]) {
          isWebp = false;
          break;
        }
      }
      if (isWebp) return "webp";
    }
  }

  // File does not match any known image signature
  return null;
}

/**
 * Validates a file is a real image using magic bytes
 * 
 * @param file - The File object to validate
 * @returns Promise resolving to validation result
 */
export async function validateImageFile(
  file: File,
  maxSizeBytes: number = 10 * 1024 * 1024 // 10MB default
): Promise<ImageValidationResult> {
  // Check file size first (quick check)
  if (file.size > maxSizeBytes) {
    return {
      isValid: false,
      type: null,
      error: `File size exceeds ${maxSizeBytes / (1024 * 1024)}MB limit`,
    };
  }

  // Check if file is empty
  if (file.size === 0) {
    return {
      isValid: false,
      type: null,
      error: "File is empty",
    };
  }

  // Detect actual file type using magic bytes
  const detectedType = await detectRealImageType(file);

  if (detectedType === null) {
    return {
      isValid: false,
      type: null,
      error: "File is not a valid image format. Only PNG, JPEG, WEBP, and GIF are allowed.",
    };
  }

  return {
    isValid: true,
    type: detectedType,
  };
}

/**
 * Converts detected image type to MIME type
 * Useful for setting correct Content-Type headers when uploading
 */
export function imageTypeToMimeType(type: ImageType): string {
  const mimeMap: Record<ImageType, string> = {
    png: "image/png",
    jpeg: "image/jpeg",
    webp: "image/webp",
    gif: "image/gif",
  };
  return mimeMap[type];
}
